import { lazy } from 'react';
import { BlobDetailsProvider } from 'src/components/pages/mymes/details/context';
import { BlobContextWrapper } from 'src/components/pages/mymes/editor/blobs/editor/BlobContextWrapper';
import { EditorContextWrapper } from 'src/components/pages/mymes/editor/EditorContextWrapper';
import { PremiumRequiredPageBlocker } from 'src/components/pages/others/PremiumRequiredPageBlocker';
import { LazyLoader } from './LazyLoader';

// INTRESTS PAGES
const MymeDetailsPage = LazyLoader(lazy(() => import('src/components/pages/mymes/details/MymeDetailsPage')));
const FinishMymePage = LazyLoader(lazy(() => import('src/components/pages/mymes/details/content/FinishMymePage')));
const ContentDetailsPage = LazyLoader(lazy(() => import('src/components/pages/mymes/details/content/ContentPage')));
const LocationBasedPage = LazyLoader(lazy(() => import('src/components/pages/mymes/locationBased/LocationBasedPage')));
const TimeBasedPage = LazyLoader(lazy(() => import('src/components/pages/mymes/timeBased/TimeBasedPage')));
const CoursesPage = LazyLoader(lazy(() => import('src/components/pages/mymes/courses/CoursesPage')));

// -- EDITOR
const CreatorStepPage = LazyLoader(lazy(() => import('src/components/pages/mymes/editor/creatorStep/CreatorStepPage')));
const DetailsStepPage = LazyLoader(lazy(() => import('src/components/pages/mymes/editor/detailsStep/DetailsStepPage')));
const ContentStepPage = LazyLoader(lazy(() => import('src/components/pages/mymes/editor/contentStep/ContentStepPage')));
const FinishStepPage = LazyLoader(lazy(() => import('src/components/pages/mymes/editor/finishStep/FinishStepPage')));
const EditorBlockedPage = LazyLoader(lazy(() => import('src/components/pages/mymes/editor/EditorBlockedPage')));

// BLOBS PAGES
const BlobsLibraryPage = LazyLoader(
  lazy(() => import('src/components/pages/mymes/editor/blobs/library/BlobsLibraryPage'))
);
const BlobCreatorPage = LazyLoader(
  lazy(() => import('src/components/pages/mymes/editor/blobs/editor/create/BlobCreatorPage'))
);
const BlobEditorPage = LazyLoader(
  lazy(() => import('src/components/pages/mymes/editor/blobs/editor/edit/BlobEditorPage'))
);

export const mymeRelatedPageRoutes = {
  path: 'mymes',
  element: <BlobDetailsProvider />,
  children: [
    {
      path: 'details',
      element: <BlobDetailsProvider />,
      children: [
        {
          path: ':mymeId',
          element: <MymeDetailsPage type='auth-custom' title='pages.mymes.details.title_head' />
        },
        {
          path: ':mymeId/blob/content/:blobId',
          element: <ContentDetailsPage type='auth-custom' title='pages.mymes.details.content.title_head' />
        },
        {
          path: ':mymeId/finish',
          element: <FinishMymePage type='auth-custom' title='pages.mymes.details.finishPage.title_head' />
        }
      ]
    },
    {
      path: 'location-based',
      element: (
        <LocationBasedPage
          type='auth-default'
          title='pages.my_mymes.location_based.title_head'
          header={{ text: 'pages.my_mymes.location_based.title', size: 'h4' }}
        />
      )
    },
    {
      path: 'time-based',
      element: (
        <TimeBasedPage
          type='auth-default'
          title='pages.my_mymes.time_based.title_head'
          header={{ text: 'pages.my_mymes.time_based.title', size: 'h4' }}
        />
      )
    },
    {
      path: 'courses',
      element: (
        <CoursesPage
          type='auth-default'
          title='pages.my_mymes.courses.title_head'
          header={{ text: 'pages.my_mymes.courses.title', size: 'h4' }}
        />
      )
    },
    {
      path: 'content-blocked',
      element: <EditorBlockedPage type='auth-custom' title='layout.blocked_content.desktop_only_title_head' />
    },
    {
      path: 'editor',
      element: (
        <PremiumRequiredPageBlocker>
          <EditorContextWrapper />
        </PremiumRequiredPageBlocker>
      ),
      children: [
        {
          path: 'create',
          element: <CreatorStepPage type='auth-custom' title='pages.mymes.editor.creator_step.title_head' />
        },
        {
          path: 'details/:mymeId',
          element: <DetailsStepPage type='auth-custom' title='pages.mymes.editor.details_step.title_head' />
        },
        {
          path: 'content/:mymeId',
          element: <ContentStepPage type='auth-custom' title='pages.mymes.editor.content_step.title_head' />
        },
        {
          path: 'finish/:mymeId',
          element: <FinishStepPage type='auth-custom' title='pages.mymes.editor.finish_step.title_head' />
        },
        {
          path: 'blobs',
          element: <BlobContextWrapper />,
          children: [
            {
              path: 'library',
              element: (
                <BlobsLibraryPage
                  type='auth-default'
                  withMarginTop
                  title='pages.mymes.editor.blobs.library.title_head'
                  header={{ text: 'pages.mymes.editor.blobs.library.title_head' }}
                />
              )
            },
            {
              path: 'create',
              element: <BlobCreatorPage type='auth-custom' title='pages.mymes.editor.blobs.creator.title_head' />
            },
            {
              path: 'edit/:blobId',
              element: <BlobEditorPage type='auth-custom' title='pages.mymes.editor.blobs.editor.title_head' />
            }
          ]
        }
      ]
    }
  ]
};

export type MymeRelatedPageTitle =
  | 'pages.mymes.details.title_head'
  | 'pages.my_mymes.location_based.title_head'
  | 'pages.my_mymes.time_based.title_head'
  | 'pages.my_mymes.courses.title_head'
  | 'layout.blocked_content.desktop_only_title_head'
  | 'pages.mymes.editor.creator_step.title_head'
  | 'pages.mymes.editor.details_step.title_head'
  | 'pages.mymes.editor.content_step.title_head'
  | 'pages.mymes.editor.finish_step.title_head'
  | 'pages.mymes.editor.blobs.library.title_head'
  | 'pages.mymes.editor.blobs.creator.title_head'
  | 'pages.mymes.editor.blobs.editor.title_head'
  | 'pages.mymes.details.content.title_head'
  | 'pages.mymes.details.finishPage.title_head';
